import { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Animation } from '@babylonjs/core/Animations/animation';
import { HighlightLayer } from '@babylonjs/core/Layers/highlightLayer';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';

import '@babylonjs/core/Animations/animatable';
import '@babylonjs/core/Meshes/Builders/sphereBuilder';
import '@babylonjs/core/Meshes/Builders/planeBuilder';
import '@babylonjs/core/Meshes/Builders/tubeBuilder';
import { GlowLayer } from '@babylonjs/core/Layers/glowLayer';
import '@babylonjs/core/Layers/effectLayerSceneComponent';

/**
 * 树节点数据
 */
interface TreeNodeData {
  value: number;
  left: TreeNodeData | null;
  right: TreeNodeData | null;
}

/**
 * 可视化树节点
 */
interface TreeNodeElement {
  mesh: Mesh;
  label: Mesh;
  leftEdge: Mesh | null;
  rightEdge: Mesh | null;
  data: TreeNodeData;
  position: Vector3;
  depth: number;
}

/**
 * 二叉树可视化器
 */
export class BinaryTreeVisualizer {
  private scene: Scene;
  private highlightLayer: HighlightLayer;
  private glowLayer: GlowLayer;
  private nodes: Map<number, TreeNodeElement> = new Map();
  private edges: Mesh[] = [];  // 存储所有边，用于清理
  private root: TreeNodeData | null = null;
  private nodeIdCounter = 0;
  
  // 可视化参数
  private readonly nodeRadius = 0.5;
  private readonly levelHeight = 2;
  private readonly baseSpacing = 4;
  
  // 颜色 - 柔和马卡龙色系
  private readonly colors = {
    default: new Color3(0.55, 0.75, 0.95),    // 天空蓝
    root: new Color3(1.0, 0.7, 0.5),          // 桃色
    visiting: new Color3(1.0, 0.85, 0.4),     // 暖黄色
    visited: new Color3(0.55, 0.88, 0.7),     // 薄荷绿
    found: new Color3(1.0, 0.6, 0.65),        // 珊瑚粉
    edge: new Color3(0.7, 0.7, 0.8),          // 淡紫灰
  };

  constructor(scene: Scene, highlightLayer: HighlightLayer) {
    this.scene = scene;
    this.highlightLayer = highlightLayer;
    
    // 创建发光层 - 轻微发光
    this.glowLayer = new GlowLayer('treeGlow', scene, {
      blurKernelSize: 32
    });
    this.glowLayer.intensity = 0.3;
  }

  /**
   * 从数组创建二叉搜索树
   */
  async createFromArray(array: number[]): Promise<void> {
    this.clear();
    
    for (const value of array) {
      this.insertSync(value);
    }
    
    console.log(`✅ 已创建二叉树 ${this.nodes.size} 个节点`);
  }

  /**
   * 同步插入节点（无动画）
   */
  insertSync(value: number): void {
    if (!this.root) {
      this.root = { value, left: null, right: null };
      const element = this.createNodeElementSync(this.root, new Vector3(0, 4, 0), 0);
      this.nodes.set(this.nodeIdCounter++, element);
      this.setNodeColor(element, this.colors.root);
    } else {
      this.insertRecursiveSync(this.root, value, new Vector3(0, 4, 0), 0);
    }
  }

  /**
   * 同步递归插入
   */
  private insertRecursiveSync(
    node: TreeNodeData,
    value: number,
    parentPos: Vector3,
    depth: number
  ): void {
    const spacing = this.baseSpacing / Math.pow(2, depth);
    const newDepth = depth + 1;
    const y = 4 - newDepth * this.levelHeight;
    
    if (value < node.value) {
      if (!node.left) {
        node.left = { value, left: null, right: null };
        const x = parentPos.x - spacing;
        const position = new Vector3(x, y, 0);
        const element = this.createNodeElementSync(node.left, position, newDepth);
        this.nodes.set(this.nodeIdCounter++, element);
        this.createEdge(parentPos, position, 'left');
      } else {
        this.insertRecursiveSync(node.left, value, new Vector3(parentPos.x - spacing, y, 0), newDepth);
      }
    } else {
      if (!node.right) {
        node.right = { value, left: null, right: null };
        const x = parentPos.x + spacing;
        const position = new Vector3(x, y, 0);
        const element = this.createNodeElementSync(node.right, position, newDepth);
        this.nodes.set(this.nodeIdCounter++, element);
        this.createEdge(parentPos, position, 'right');
      } else {
        this.insertRecursiveSync(node.right, value, new Vector3(parentPos.x + spacing, y, 0), newDepth);
      }
    }
  }

  /**
   * 同步创建节点元素（无动画）
   */
  private createNodeElementSync(
    data: TreeNodeData,
    position: Vector3,
    depth: number
  ): TreeNodeElement {
    const mesh = MeshBuilder.CreateSphere(
      `treeNode_${data.value}_${this.nodeIdCounter}`,
      { diameter: this.nodeRadius * 2 },
      this.scene
    );
    mesh.position = position.clone();
    
    const material = new StandardMaterial(`treeNodeMat_${data.value}`, this.scene);
    material.diffuseColor = this.colors.default;
    material.specularColor = new Color3(0.3, 0.3, 0.3);
    mesh.material = material;
    
    const label = this.createLabel(data.value.toString(), mesh);
    
    return {
      mesh,
      label,
      leftEdge: null,
      rightEdge: null,
      data,
      position: position.clone(),
      depth
    };
  }

  /**
   * 插入节点
   */
  async insert(value: number): Promise<void> {
    if (!this.root) {
      this.root = { value, left: null, right: null };
      const element = await this.createNodeElement(this.root, new Vector3(0, 4, 0), 0);
      this.nodes.set(this.nodeIdCounter++, element);
      this.setNodeColor(element, this.colors.root);
    } else {
      await this.insertRecursive(this.root, value, new Vector3(0, 4, 0), 0);
    }
  }

  /**
   * 递归插入
   */
  private async insertRecursive(
    node: TreeNodeData,
    value: number,
    parentPos: Vector3,
    depth: number
  ): Promise<void> {
    const spacing = this.baseSpacing / Math.pow(2, depth);
    const newDepth = depth + 1;
    const y = 4 - newDepth * this.levelHeight;
    
    if (value < node.value) {
      if (!node.left) {
        node.left = { value, left: null, right: null };
        const x = parentPos.x - spacing;
        const position = new Vector3(x, y, 0);
        const element = await this.createNodeElement(node.left, position, newDepth);
        this.nodes.set(this.nodeIdCounter++, element);
        
        // 创建连接边
        this.createEdge(parentPos, position, 'left');
      } else {
        await this.insertRecursive(node.left, value, new Vector3(parentPos.x - spacing, y, 0), newDepth);
      }
    } else {
      if (!node.right) {
        node.right = { value, left: null, right: null };
        const x = parentPos.x + spacing;
        const position = new Vector3(x, y, 0);
        const element = await this.createNodeElement(node.right, position, newDepth);
        this.nodes.set(this.nodeIdCounter++, element);
        
        // 创建连接边
        this.createEdge(parentPos, position, 'right');
      } else {
        await this.insertRecursive(node.right, value, new Vector3(parentPos.x + spacing, y, 0), newDepth);
      }
    }
  }

  /**
   * 创建节点元素
   */
  private async createNodeElement(
    data: TreeNodeData,
    position: Vector3,
    depth: number
  ): Promise<TreeNodeElement> {
    // 创建球体
    const mesh = MeshBuilder.CreateSphere(
      `treeNode_${data.value}`,
      { diameter: this.nodeRadius * 2 },
      this.scene
    );
    mesh.position = new Vector3(position.x, 8, position.z); // 从上方落下
    
    // 材质
    const material = new StandardMaterial(`treeNodeMat_${data.value}`, this.scene);
    material.diffuseColor = this.colors.default;
    material.specularColor = new Color3(0.3, 0.3, 0.3);
    mesh.material = material;
    
    // 标签
    const label = this.createLabel(data.value.toString(), mesh);
    
    // 入场动画
    await this.animateNodeIn(mesh, position);
    
    return {
      mesh,
      label,
      leftEdge: null,
      rightEdge: null,
      data,
      position: position.clone(),
      depth
    };
  }

  /**
   * 创建边
   */
  private createEdge(from: Vector3, to: Vector3, side: 'left' | 'right'): Mesh {
    const tube = MeshBuilder.CreateTube(
      `edge_${side}`,
      {
        path: [from, to],
        radius: 0.03,
        tessellation: 8
      },
      this.scene
    );
    
    const material = new StandardMaterial('edgeMat', this.scene);
    material.diffuseColor = this.colors.edge;
    material.emissiveColor = new Color3(0.1, 0.1, 0.1);
    tube.material = material;
    
    // 存储边以便清理
    this.edges.push(tube);
    
    return tube;
  }

  /**
   * 创建标签
   */
  private createLabel(text: string, parent: Mesh): Mesh {
    const plane = MeshBuilder.CreatePlane(
      `treeLabel_${text}`,
      { width: 0.6, height: 0.4 },
      this.scene
    );
    
    const texture = new DynamicTexture(
      `treeLabelTex_${text}`,
      { width: 128, height: 64 },
      this.scene,
      false
    );
    texture.hasAlpha = true;
    texture.drawText(text, null, null, 'bold 32px Arial', 'white', 'transparent', true);
    
    const material = new StandardMaterial(`treeLabelMat_${text}`, this.scene);
    material.diffuseTexture = texture;
    material.emissiveColor = new Color3(1, 1, 1);
    material.disableLighting = true;
    material.backFaceCulling = false;
    plane.material = material;
    
    plane.parent = parent;
    plane.position.z = -this.nodeRadius - 0.3;
    plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
    
    return plane;
  }

  /**
   * 节点入场动画
   */
  private animateNodeIn(mesh: Mesh, target: Vector3): Promise<void> {
    return new Promise(resolve => {
      mesh.scaling = new Vector3(0.1, 0.1, 0.1);
      
      const posAnim = new Animation('dropIn', 'position.y', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      posAnim.setKeys([
        { frame: 0, value: 8 },
        { frame: 15, value: target.y - 0.3 },
        { frame: 20, value: target.y }
      ]);
      
      const scaleAnim = new Animation('scaleIn', 'scaling', 60, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CONSTANT);
      scaleAnim.setKeys([
        { frame: 0, value: new Vector3(0.1, 0.1, 0.1) },
        { frame: 15, value: new Vector3(1.1, 1.1, 1.1) },
        { frame: 20, value: new Vector3(1, 1, 1) }
      ]);
      
      mesh.animations = [posAnim, scaleAnim];
      this.scene.beginAnimation(mesh, 0, 20, false, 1, resolve);
    });
  }

  /**
   * 中序遍历
   */
  async inorderTraversal(onVisit?: (value: number) => void): Promise<number[]> {
    const result: number[] = [];
    await this.inorderRecursive(this.root, result, onVisit);
    return result;
  }

  private async inorderRecursive(
    node: TreeNodeData | null,
    result: number[],
    onVisit?: (value: number) => void
  ): Promise<void> {
    if (!node) return;
    
    await this.inorderRecursive(node.left, result, onVisit);
    
    // 访问当前节点
    const element = this.findElement(node.value);
    if (element) {
      this.setNodeColor(element, this.colors.visiting);
      this.highlightLayer.addMesh(element.mesh, Color3.Yellow());
      await this.delay(500);
      
      result.push(node.value);
      if (onVisit) onVisit(node.value);
      
      this.setNodeColor(element, this.colors.visited);
      this.highlightLayer.removeMesh(element.mesh);
    }
    
    await this.inorderRecursive(node.right, result, onVisit);
  }

  /**
   * 前序遍历
   */
  async preorderTraversal(onVisit?: (value: number) => void): Promise<number[]> {
    const result: number[] = [];
    await this.preorderRecursive(this.root, result, onVisit);
    return result;
  }

  private async preorderRecursive(
    node: TreeNodeData | null,
    result: number[],
    onVisit?: (value: number) => void
  ): Promise<void> {
    if (!node) return;
    
    // 访问当前节点
    const element = this.findElement(node.value);
    if (element) {
      this.setNodeColor(element, this.colors.visiting);
      this.highlightLayer.addMesh(element.mesh, Color3.Yellow());
      await this.delay(500);
      
      result.push(node.value);
      if (onVisit) onVisit(node.value);
      
      this.setNodeColor(element, this.colors.visited);
      this.highlightLayer.removeMesh(element.mesh);
    }
    
    await this.preorderRecursive(node.left, result, onVisit);
    await this.preorderRecursive(node.right, result, onVisit);
  }

  /**
   * 后序遍历
   */
  async postorderTraversal(onVisit?: (value: number) => void): Promise<number[]> {
    const result: number[] = [];
    await this.postorderRecursive(this.root, result, onVisit);
    return result;
  }

  private async postorderRecursive(
    node: TreeNodeData | null,
    result: number[],
    onVisit?: (value: number) => void
  ): Promise<void> {
    if (!node) return;
    
    await this.postorderRecursive(node.left, result, onVisit);
    await this.postorderRecursive(node.right, result, onVisit);
    
    // 访问当前节点
    const element = this.findElement(node.value);
    if (element) {
      this.setNodeColor(element, this.colors.visiting);
      this.highlightLayer.addMesh(element.mesh, Color3.Yellow());
      await this.delay(500);
      
      result.push(node.value);
      if (onVisit) onVisit(node.value);
      
      this.setNodeColor(element, this.colors.visited);
      this.highlightLayer.removeMesh(element.mesh);
    }
  }

  /**
   * 搜索节点
   */
  async search(value: number): Promise<boolean> {
    return this.searchRecursive(this.root, value);
  }

  private async searchRecursive(node: TreeNodeData | null, target: number): Promise<boolean> {
    if (!node) return false;
    
    const element = this.findElement(node.value);
    if (element) {
      this.setNodeColor(element, this.colors.visiting);
      this.highlightLayer.addMesh(element.mesh, Color3.Yellow());
      await this.delay(500);
    }
    
    if (node.value === target) {
      if (element) {
        this.setNodeColor(element, this.colors.found);
      }
      return true;
    }
    
    if (element) {
      this.setNodeColor(element, this.colors.default);
      this.highlightLayer.removeMesh(element.mesh);
    }
    
    if (target < node.value) {
      return this.searchRecursive(node.left, target);
    } else {
      return this.searchRecursive(node.right, target);
    }
  }

  /**
   * 查找节点元素
   */
  private findElement(value: number): TreeNodeElement | undefined {
    for (const element of this.nodes.values()) {
      if (element.data.value === value) {
        return element;
      }
    }
    return undefined;
  }

  /**
   * 设置节点颜色
   */
  private setNodeColor(element: TreeNodeElement, color: Color3): void {
    if (element.mesh.material instanceof StandardMaterial) {
      element.mesh.material.diffuseColor = color;
    }
  }

  /**
   * 重置颜色
   */
  async resetColors(): Promise<void> {
    for (const element of this.nodes.values()) {
      if (element.depth === 0) {
        this.setNodeColor(element, this.colors.root);
      } else {
        this.setNodeColor(element, this.colors.default);
      }
      this.highlightLayer.removeMesh(element.mesh);
    }
  }

  /**
   * 高亮节点（按索引）- 增强版
   */
  async highlight(index: number, duration = 500): Promise<void> {
    const element = this.getNodeByIndex(index);
    if (element) {
      this.setNodeColor(element, this.colors.visiting);
      this.highlightLayer.addMesh(element.mesh, Color3.Yellow());
      
      // 添加发光和脉冲效果
      this.glowLayer.intensity = 0.5;
      await this.animatePulse(element.mesh, duration * 0.8);
      this.glowLayer.intensity = 0.3;
    }
  }

  /**
   * 取消高亮（按索引）
   */
  async unhighlight(index: number): Promise<void> {
    const element = this.getNodeByIndex(index);
    if (element) {
      if (element.depth === 0) {
        this.setNodeColor(element, this.colors.root);
      } else {
        this.setNodeColor(element, this.colors.default);
      }
      this.highlightLayer.removeMesh(element.mesh);
    }
  }

  /**
   * 标记节点为已访问 - 增强版
   */
  async markSorted(indices: number[], duration = 300): Promise<void> {
    for (const index of indices) {
      const element = this.getNodeByIndex(index);
      if (element) {
        this.setNodeColor(element, this.colors.visited);
        this.highlightLayer.addMesh(element.mesh, Color3.Green());
        
        // 庆祝效果
        await this.animateCelebrate(element.mesh);
      }
    }
  }

  /**
   * 脉冲动画
   */
  private animatePulse(mesh: Mesh, duration: number): Promise<void> {
    return new Promise(resolve => {
      const frames = Math.round(duration / 16.67);
      
      const animation = new Animation(
        'pulse',
        'scaling',
        60,
        Animation.ANIMATIONTYPE_VECTOR3,
        Animation.ANIMATIONLOOPMODE_CONSTANT
      );
      
      animation.setKeys([
        { frame: 0, value: new Vector3(1, 1, 1) },
        { frame: frames * 0.25, value: new Vector3(1.3, 1.3, 1.3) },
        { frame: frames * 0.5, value: new Vector3(1, 1, 1) },
        { frame: frames * 0.75, value: new Vector3(1.2, 1.2, 1.2) },
        { frame: frames, value: new Vector3(1, 1, 1) }
      ]);
      
      mesh.animations = [animation];
      this.scene.beginAnimation(mesh, 0, frames, false, 1, resolve);
    });
  }

  /**
   * 庆祝动画
   */
  private animateCelebrate(mesh: Mesh): Promise<void> {
    return new Promise(resolve => {
      // 跳跃动画
      const startY = mesh.position.y;
      const jumpAnim = new Animation('jump', 'position.y', 60, Animation.ANIMATIONTYPE_FLOAT, Animation.ANIMATIONLOOPMODE_CONSTANT);
      jumpAnim.setKeys([
        { frame: 0, value: startY },
        { frame: 6, value: startY + 0.8 },
        { frame: 12, value: startY }
      ]);
      
      // 缩放动画
      const scaleAnim = new Animation('celebrateScale', 'scaling', 60, Animation.ANIMATIONTYPE_VECTOR3, Animation.ANIMATIONLOOPMODE_CONSTANT);
      scaleAnim.setKeys([
        { frame: 0, value: new Vector3(1, 1, 1) },
        { frame: 3, value: new Vector3(1.3, 0.8, 1.3) },
        { frame: 6, value: new Vector3(0.9, 1.2, 0.9) },
        { frame: 9, value: new Vector3(1.1, 0.95, 1.1) },
        { frame: 12, value: new Vector3(1, 1, 1) }
      ]);
      
      mesh.animations = [jumpAnim, scaleAnim];
      this.scene.beginAnimation(mesh, 0, 12, false, 2, resolve);
    });
  }

  /**
   * 根据索引获取节点
   */
  private getNodeByIndex(index: number): TreeNodeElement | undefined {
    return this.nodes.get(index);
  }

  /**
   * 重置可视化
   */
  async reset(): Promise<void> {
    await this.resetColors();
  }

  /**
   * 清除
   */
  clear(): void {
    for (const element of this.nodes.values()) {
      element.label.dispose();
      element.mesh.dispose();
      if (element.leftEdge) element.leftEdge.dispose();
      if (element.rightEdge) element.rightEdge.dispose();
    }
    // 清理所有边
    for (const edge of this.edges) {
      edge.dispose();
    }
    this.edges = [];
    this.nodes.clear();
    this.root = null;
    this.nodeIdCounter = 0;
    this.highlightLayer.removeAllMeshes();
  }

  /**
   * 延迟
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
